/* eslint-disable @typescript-eslint/naming-convention */
import { useState, useEffect } from "react";
import {
  useStripe,
  useElements,
  PaymentElement,
} from "@stripe/react-stripe-js";
import { useMutation, useLazyQuery, useSubscription } from "@apollo/client";
import STRIPE_WEBHOOK_INFORMATION from "src/graphql/subscriptions/stripe/subscription";
import MeQuery from "src/graphql/query/auth/me";
import COMPLETE_SUBSCRIPTION_SETUP from "src/graphql/query/subscription/completeSubscriptionSetup";
import { useNavigate } from "react-router-dom";
import { enqueueSnackbar } from "notistack";
import setAuthDataInLocalStorage from "src/utils/auth/LocalStorage";
import useActiveUserDetail from "src/utils/shared/useActiveUserDetail";

interface ISubscriptionData {
  maxAgents: number;
  new_plan: string;
  priceId: string;
}

interface ICheckoutForm {
  formRef: any;
  setLoader: (a: boolean) => void;
  closeModal: () => void;
  subscriptionData: ISubscriptionData;
}
const CheckoutForm: React.FC<ICheckoutForm> = ({
  formRef,
  setLoader,
  closeModal,
  subscriptionData,
}) => {
  const stripe = useStripe();
  const elements = useElements();

  const [getMeData, { data: meQueryResult }] = useLazyQuery(MeQuery, {
    fetchPolicy: "network-only",
  });

  const navigate = useNavigate();

  const [message, setMessage] = useState<String | undefined>("");
  const [isProcessing, setIsProcessing] = useState(false);

  const { stripeId: customerId } = useActiveUserDetail();

  const [completeSubscriptionSetup] = useMutation(COMPLETE_SUBSCRIPTION_SETUP);

  const { data: stripeWebhookSubscriptionResult } = useSubscription(
    STRIPE_WEBHOOK_INFORMATION,
  );

  useEffect(() => {
    if (stripeWebhookSubscriptionResult?.stripeWebhook && meQueryResult?.me) {
      const { stripeWebhook } = stripeWebhookSubscriptionResult;
      const { me } = meQueryResult;

      const mergedMe = {
        ...me,
        ...stripeWebhook,
      };

      setAuthDataInLocalStorage(mergedMe);

      navigate("/");
      closeModal();
    }
  }, [stripeWebhookSubscriptionResult, meQueryResult]);

  const handleSubmit = async (e: any) => {
    e.preventDefault();

    if (!stripe || !elements) return;

    setIsProcessing(true);
    setLoader(true);

    const { error, setupIntent } = await stripe.confirmSetup({
      elements,
      redirect: "if_required",
    });

    if (error) {
      setMessage(error.message || "Setup failed");
      setIsProcessing(false);
      setLoader(false);
      enqueueSnackbar(error.message || "Setup failed", {
        variant: "error",
      });
      return;
    }

    if (setupIntent?.status === "succeeded") {
      setMessage("Payment method added successfully 🎉");

      try {
        // Call backend to create subscription
        const { maxAgents, new_plan, priceId } = subscriptionData;
        const { data } = await completeSubscriptionSetup({
          variables: {
            customerId,
            setupIntentId: setupIntent.id,
            noOfAgents: maxAgents,
            planName: new_plan,
            priceId,
          },
        });

        const res = data?.completeSubscriptionSetup;
        if (!res) throw new Error("No server response");

        const { success, error: serverError } = res;

        if (success !== 1) {
          throw new Error(serverError || "Failed to create subscription");
        }

        getMeData();

        enqueueSnackbar("Subscription created successfully! 🎉", {
          variant: "success",
        });
      } catch (err: any) {
        console.error("Subscription creation error:", err);
        enqueueSnackbar(err.message || "Failed to create subscription", {
          variant: "error",
        });
      }
    }

    setIsProcessing(false);
    setLoader(false);
  };

  return (
    <form ref={formRef} id="payment-form" onSubmit={handleSubmit}>
      <PaymentElement id="payment-element" />
      <button
        type="submit"
        disabled={isProcessing || !stripe || !elements}
        id="submit"
      >
        {/* <span id="button-text">
          {isProcessing ? "Processing ... " : "Pay now"}
        </span> */}
      </button>
      {/* Show any error or success messages */}
      {message && <div id="payment-message">{message}</div>}
    </form>
  );
};

export default CheckoutForm;
